import { CourseDataFilters } from '../types/filtered-data';
import { Instrument } from '../types/instrument';

/**
 * Checks if there are any active filters set
 */
export function hasActiveFilters(filters: CourseDataFilters): boolean {
  return (
    filters.instruments.length > 0 ||
    filters.skillLevels.length > 0 ||
    filters.genres.length > 0 ||
    filters.topics.length > 0 ||
    filters.types.length > 0
  );
}

/**
 * Toggles a value in an array based on checked state
 */
export function toggleFilterValue(
  currentValues: string[],
  value: string,
  checked: boolean,
): string[] {
  return checked
    ? Array.from(new Set([...currentValues, value]))
    : currentValues.filter((v) => v !== value);
}

/**
 * Gets all child instruments for a parent instrument
 */
export function getChildInstruments(
  parent: Instrument,
  allInstruments: Instrument[],
): Instrument[] {
  return allInstruments.filter(
    (inst) =>
      inst.parentInstrument && inst.parentInstrument.instrumentName === parent.instrumentName,
  );
}

/**
 * Checks if an instrument has child instruments
 */
export function instrumentHasChildren(parent: Instrument, allInstruments: Instrument[]): boolean {
  return getChildInstruments(parent, allInstruments).length > 0;
}

/**
 * Updates the instruments filter array with the appropriate logic
 * for parent and child instruments
 */
export function updateInstrumentsFilter(
  checked: boolean,
  instrument: Instrument,
  currentInstruments: string[],
  allInstruments: Instrument[],
): string[] {
  const isParent = !instrument.parentInstrument;
  let newInstruments = [...currentInstruments];

  if (isParent) {
    // If parent, toggle all children too
    const children = getChildInstruments(instrument, allInstruments).map(
      (child) => child.instrumentName,
    );

    newInstruments = checked
      ? Array.from(new Set([...newInstruments, instrument.instrumentName, ...children]))
      : newInstruments.filter((i) => i !== instrument.instrumentName && !children.includes(i));
  } else {
    // If child, just toggle this one
    newInstruments = toggleFilterValue(newInstruments, instrument.instrumentName, checked);
  }

  return newInstruments;
}

/**
 * Gets a user-friendly display name for a category
 */
export function getCategoryDisplayName(categoryName: string | undefined): string {
  if (categoryName === 'da-zero') return 'Percorsi da zero';
  if (categoryName === 'piu-recenti') return 'Ultime uscite';
  if (categoryName === 'piu-visti') return 'Più seguiti';
  if (categoryName === 'masterclass') return 'Masterclass';
  return 'Categoria non trovata';
}

/**
 * Compares two arrays of course data to check if they contain the same items
 */
export function areCourseDataArraysEqual(prev: any[], newData: any[]): boolean {
  if (prev.length !== newData.length) return false;

  const prevIds = new Set(prev.map((item) => item.documentId));
  for (const item of newData) {
    if (!prevIds.has(item.documentId)) {
      return false;
    }
  }
  return true;
}
